import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, Loader2, Check, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { setToken, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * ResetPassword — landing page for the emailed reset link.
 *
 * The token comes in as ?token=... in the URL. On success the backend
 * atomically logs the user in (issues a new JWT + sets the cookie) so
 * they don't need to re-authenticate after the reset. We route to
 * /dashboard on completion.
 */
export default function ResetPassword() {
  const { search } = useLocation();
  const token = useMemo(() => new URLSearchParams(search).get("token") || "", [search]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      toast.error("Missing or invalid reset link.");
    }
  }, [token]);

  const rules = [
    { key: "len", label: "At least 8 characters", ok: password.length >= 8 },
    { key: "match", label: "Passwords match", ok: confirm && password === confirm },
  ];
  const allValid = rules.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || !allValid || !token) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (data?.token) setToken(data.token);
      setUser(data);
      toast.success("Password updated. You're logged in.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Reset failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-zinc-50 dark:bg-zinc-950" data-testid="reset-root">
      <div className="pointer-events-none absolute inset-x-0 -top-40 -z-0 h-[420px] bg-[radial-gradient(circle_at_50%_0%,rgba(16,185,129,0.18),transparent_70%)]" aria-hidden="true"/>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col items-center px-6 py-12 sm:py-16">
        <Link to="/" className="flex items-center gap-2">
          <Logo className="h-9 w-9" />
          <span className="font-heading text-lg font-bold">Syllox</span>
        </Link>

        <div className="mt-8 w-full">
          <h1 className="font-heading text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl" data-testid="reset-heading">
            Set a new password.
          </h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400" data-testid="reset-subheading">
            Choose a strong, unique password — at least 8 characters.
          </p>

          {!token && (
            <div className="mt-6 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200" data-testid="reset-no-token">
              <AlertCircle size={16} className="shrink-0"/>
              <div>
                No token found in the URL. Please open the reset link from the email we sent — the whole link matters.{" "}
                <Link to="/forgot-password" className="font-medium underline">Request a new one.</Link>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="reset-form">
            <div className="space-y-1.5">
              <Label htmlFor="reset-password">New password</Label>
              <div className="relative">
                <Input
                  id="reset-password"
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reset-confirm">Confirm password</Label>
              <Input
                id="reset-confirm"
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm-input"
              />
            </div>

            <ul className="mt-1 space-y-0.5 text-xs" data-testid="reset-rules">
              {rules.map((r) => (
                <li key={r.key} className={`flex items-center gap-1.5 ${r.ok ? "text-emerald-600" : "text-zinc-400"}`}>
                  {r.ok ? <Check size={12}/> : <X size={12}/>}
                  {r.label}
                </li>
              ))}
            </ul>

            <Button
              type="submit"
              disabled={submitting || !allValid || !token}
              className="w-full h-11 bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
              data-testid="reset-submit-btn"
            >
              {submitting ? <Loader2 size={16} className="animate-spin"/> : "Update password"}
            </Button>
          </form>

          <p className="mt-5 text-center text-sm text-zinc-600 dark:text-zinc-400">
            <Link to="/login" className="font-medium text-emerald-600 hover:underline">
              ← Back to login
            </Link>
          </p>
        </div>

        <div className="mt-auto pt-8 text-center text-xs text-zinc-400 dark:text-zinc-500">
          © {new Date().getFullYear()} Syllox · Made in India
        </div>
      </div>
    </div>
  );
}
